import PropTypes from 'prop-types';
import { Icon, useOpenClose, Modal, Button, Tooltip2, LinkButton } from '@evertrue/et-components';
import { mostRecentCreate, prioritizeCareers } from 'apps/profile/components/enrichment/enrichment-utils';
import EmptyStateMessage from 'components/lists/empty-state-message';
import ProfileCardHeader from 'apps/profile/components/profile-card-header';
import CareerEditModalBody from 'apps/profile/components/career-edit-modal-body';
import CareerEnrichmentCard from 'apps/profile/components/enrichment/career-enrichment-card';
import EnrichmentModalHeader from 'apps/profile/components/enrichment/enrichment-modal-header';
import EmploymentTabs from 'apps/profile/components/enrichment/employment-tabs';
import { useState, useEffect } from 'react';

const renderCareers = (careers) => {
  return careers.map((career) => {
    const { id, company_id, title } = career;
    return (
      <div key={id} className="profile-enrichment--item">
        <CareerEnrichmentCard data={career} key={company_id + title} />
      </div>
    );
  });
};

const CareerEnrichment = ({ contact, careers, onDelete }) => {
  const [currentEmployments, setCurrentEmployments] = useState([]);
  const [pastEmployments, setPastEmployments] = useState([]);
  const [totalEmployments, setTotalEmployments] = useState(0);
  const [activeTab, setActiveTab] = useState('current');
  const [isModalOpen, openModal, closeModal] = useOpenClose();
  const [hasAnyData, setHasAnyData] = useState(false);

  useEffect(() => {
    const current = careers.filter(
      (employment) => (employment.status === 'current' || employment.status === null) && !employment.rejected
    );
    const past = careers.filter((employment) => employment.status === 'past' && !employment.rejected);
    setCurrentEmployments(prioritizeCareers(current));
    setPastEmployments(prioritizeCareers(past));
    setTotalEmployments(current.length + past.length);

    // Check if there's ANY data (active or rejected) to show edit icon
    setHasAnyData(careers.length > 0);

    // Set default active tab based on available data
    if (current.length > 0) {
      setActiveTab('current');
    } else if (past.length > 0) {
      setActiveTab('previous');
    }
  }, [careers]);

  const renderEditModal = (employments) => (
    <Modal isOpen={isModalOpen} closeModal={closeModal} size="500px">
      <EnrichmentModalHeader
        headerText={contact.name + "'s Employments"}
        updateDate={mostRecentCreate(employments)}
        closeModal={closeModal}
      ></EnrichmentModalHeader>
      <CareerEditModalBody
        contact_id={contact.id}
        employmentData={prioritizeCareers(employments)}
        onDelete={onDelete}
      />
    </Modal>
  );

  // Build tabs array based on available data
  const tabs = [];
  if (currentEmployments.length > 0) {
    tabs.push({ key: 'current', label: 'CURRENT EMPLOYMENT' });
  }
  if (pastEmployments.length > 0) {
    tabs.push({ key: 'previous', label: 'PREVIOUS EMPLOYMENT' });
  }

  // Get the employments to display for the active tab
  const activeEmployments = activeTab === 'current' ? currentEmployments : pastEmployments;
  const additionalCount = activeEmployments.length > 1 ? activeEmployments.length - 1 : 0;

  return (
    <>
      <ProfileCardHeader
        title="Career"
        updateDate={totalEmployments > 0 ? mostRecentCreate([...currentEmployments, ...pastEmployments]) : null}
        showDateAsTooltip={true}
      >
        {hasAnyData && (
          <Button type="simple" onClick={isModalOpen ? closeModal : openModal}>
            <Icon title="Edit career information" icon="edit" />
          </Button>
        )}
      </ProfileCardHeader>

      {/* Show tabs if we have any employment data */}
      {tabs.length > 0 && <EmploymentTabs tabs={tabs} activeTab={activeTab} onTabClick={setActiveTab} />}

      {/* Body content */}
      <div className="profile--enrichment-inner">
        {totalEmployments > 0 ? (
          <>
            {/* Show the first employment of the active tab */}
            {activeEmployments.length > 0 && (
              <div className="profile--enrichment-group">
                <div className="profile--enrichment-group-data">{renderCareers([activeEmployments[0]])}</div>
              </div>
            )}
          </>
        ) : (
          <EmptyStateMessage hideIcon text="No enriched career data available for this constituent" />
        )}
      </div>

      {/* Footer with "more" button */}
      {additionalCount > 0 && (
        <div className="profile--enrichment-footer">
          <Tooltip2
            contentWidth={175}
            trigger={
              <LinkButton onClick={isModalOpen ? closeModal : openModal} className="profile--enrichment-more-btn">
                (+ {additionalCount} more)
              </LinkButton>
            }
          >
            <div className="centered-tooltip">
              View {additionalCount} more employment record{additionalCount > 1 ? 's' : ''}
            </div>
          </Tooltip2>
        </div>
      )}

      {/* Render modal once */}
      {renderEditModal(careers)}
    </>
  );
};

CareerEnrichment.propTypes = {
  contact: PropTypes.object,
  careers: PropTypes.array,
  onDelete: PropTypes.func,
};

export default CareerEnrichment;
